import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { ChevronDown, ChevronUp, X } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import PermissionsTable from "./PermissionsTable";
import {
  formatCategory,
  getAllowedCategories,
  METHOD_LABELS,
} from "./rolePermissions";

const FIELD_NAME = "category_permissions";

const CategoryMethodToggle = ({ category, method, endpoints, onToggle }) => {
  const methodEndpoints = endpoints.filter(p => p.method === method);
  const isAllAllowed =
    methodEndpoints.length > 0 && methodEndpoints.every(p => p.is_allowed);
  const isSomeAllowed = methodEndpoints.some(p => p.is_allowed);
  const id = `role-${category}-${method}`;

  return (
    <Field orientation="horizontal" className="tw:w-auto">
      <Checkbox
        id={id}
        checked={isAllAllowed}
        indeterminate={isSomeAllowed && !isAllAllowed}
        disabled={methodEndpoints.length === 0}
        onCheckedChange={checked => onToggle(method, checked)}
      />
      <FieldLabel htmlFor={id}>{METHOD_LABELS[method]}</FieldLabel>
    </Field>
  );
};

CategoryMethodToggle.propTypes = {
  category: PropTypes.string.isRequired,
  method: PropTypes.string.isRequired,
  endpoints: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const PermissionCategories = ({ value, onChange, error }) => {
  const [selectedCategories, setSelectedCategories] = useState(() =>
    getAllowedCategories(value)
  );
  const [openCategories, setOpenCategories] = useState([]);

  const sortedSelected = useMemo(
    () => [...selectedCategories].sort(),
    [selectedCategories]
  );
  // base-ui matches selection by reference, so the selected chips are looked
  // up in the same item set the dropdown is built from.
  const items = useMemo(
    () =>
      Object.keys(value)
        .sort()
        .map(category => ({
          value: category,
          label: formatCategory(category),
        })),
    [value]
  );
  const selectedItems = items.filter(item =>
    selectedCategories.includes(item.value)
  );
  const availableItems = items.filter(
    item => !selectedCategories.includes(item.value)
  );

  const setCategoryEndpoints = (category, endpoints) =>
    onChange({ ...value, [category]: endpoints });

  const handleCategoriesChange = nextItems => {
    const nextCategories = nextItems.map(item => item.value);
    const removedCategories = selectedCategories.filter(
      category => !nextCategories.includes(category)
    );
    setSelectedCategories(nextCategories);
    setOpenCategories(current =>
      current.filter(category => nextCategories.includes(category))
    );
    // A removed category must not keep granting access it no longer shows.
    if (removedCategories.length > 0) {
      onChange({
        ...value,
        ...Object.fromEntries(
          removedCategories.map(category => [
            category,
            value[category].map(p => ({ ...p, is_allowed: false })),
          ])
        ),
      });
    }
  };

  const handleRemoveCategory = category =>
    handleCategoriesChange(
      selectedItems.filter(item => item.value !== category)
    );

  const handleToggleMethod = (category, method, isAllowed) =>
    setCategoryEndpoints(
      category,
      value[category].map(p =>
        p.method === method ? { ...p, is_allowed: isAllowed } : p
      )
    );

  const isAnyOpen = openCategories.length > 0;
  const handleExpandCollapseAll = () =>
    setOpenCategories(isAnyOpen ? [] : sortedSelected);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor="role-permission-category">
          Permission Category
        </FieldLabel>
        <Combobox
          items={availableItems}
          value={selectedItems}
          onValueChange={handleCategoriesChange}
          multiple
        >
          <ComboboxChips>
            <ComboboxValue>
              {selected =>
                selected.map(item => (
                  <ComboboxChip key={item.value}>{item.label}</ComboboxChip>
                ))
              }
            </ComboboxValue>
            <ComboboxChipsInput
              id="role-permission-category"
              placeholder={
                selectedItems.length === 0 ? "Add permission categories" : ""
              }
              aria-invalid={error ? true : undefined}
            />
          </ComboboxChips>
          <ComboboxContent>
            <ComboboxEmpty>No matches</ComboboxEmpty>
            <ComboboxList>
              {item => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <FieldError>{error}</FieldError>
      </Field>

      {sortedSelected.length > 0 && (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExpandCollapseAll}
            >
              {isAnyOpen ? (
                <ChevronUp data-icon="inline-start" />
              ) : (
                <ChevronDown data-icon="inline-start" />
              )}
              {isAnyOpen ? "Collapse All" : "Expand All"}
            </Button>
          </div>
          <Accordion
            multiple
            value={openCategories}
            onValueChange={setOpenCategories}
            className="tw:gap-2"
          >
            {sortedSelected.map(category => (
              <AccordionItem
                key={category}
                value={category}
                className="tw:rounded-lg tw:border tw:border-border tw:px-3"
              >
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2">
                  {/* The method checkboxes sit beside the trigger, not inside
                      it, because a checkbox nested in a button is not
                      reachable by keyboard or screen readers. */}
                  <div className="tw:min-w-48 tw:flex-1">
                    <AccordionTrigger className="tw:font-semibold">
                      {formatCategory(category)}
                    </AccordionTrigger>
                  </div>
                  {Object.keys(METHOD_LABELS).map(method => (
                    <CategoryMethodToggle
                      key={method}
                      category={category}
                      method={method}
                      endpoints={value[category]}
                      onToggle={(m, isAllowed) =>
                        handleToggleMethod(category, m, isAllowed)
                      }
                    />
                  ))}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${formatCategory(category)}`}
                    onClick={() => handleRemoveCategory(category)}
                  >
                    <X />
                  </Button>
                </div>
                <AccordionContent>
                  <PermissionsTable
                    permissions={value[category]}
                    onSetPermissions={endpoints =>
                      setCategoryEndpoints(category, endpoints)
                    }
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
};

PermissionCategories.propTypes = {
  value: PropTypes.objectOf(PropTypes.array).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

const RolePermissionsField = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={({ field }) => (
        <PermissionCategories
          value={field.value}
          onChange={field.onChange}
          error={errors[FIELD_NAME]?.message}
        />
      )}
    />
  );
};

export default RolePermissionsField;
